<template>
  <AdminPageView :loading="loading" :loading-error-message="loadingErrorMessage" :actions-only-on-top="true" title="Paramètres">
    <template #actions>
      <button class="btn btn-outline-success text-nowrap" :disabled="isSubmitting" @click="openCreate">
        <i class="fa-solid fa-plus pe-2"></i>Ajouter une bannière
      </button>
    </template>

    <section class="mb-4">
      <h3 class="h5 border-bottom pb-2">Réglages généraux</h3>
      <div class="d-flex justify-content-center flex-wrap gap-2">
        <div class="col-12" v-for="setting in settings" :key="setting.key">
          <CheckboxComponent
            v-if="isBoolSetting(setting)"
            v-model="setting.boolValue!"
            :label="setting.title ?? setting.key"
            @update:model-value="() => saveBoolSetting(setting.key)"
          />
          <TextInputComponent
            v-else
            v-model="setting.stringValue"
            :label="setting.title ?? setting.key"
            @update:model-value="() => saveStringSetting(setting.key)"
          />
        </div>
      </div>
    </section>

    <section>
      <div class="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
        <h3 class="h5 mb-0">Gestion des bannières</h3>
      </div>

    <div v-if="editingBanner" class="card mb-3">
      <div class="card-header bg-primary">
        <h5 class="mb-0">{{ editingBanner.id === 0 ? 'Nouvelle bannière' : 'Modifier la bannière' }}</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-12 col-md-6">
            <SelectInputComponent
              v-model="editingBanner.travelId"
              label="Événement"
              placeholder="Sélectionnez un événement"
              :options="travelOptions"
              :empty-option-label="'Sélectionnez un événement'"
              :empty-option-value="0"
              :required="true"
              class="mb-3"
            />
          </div>
          <div class="col-12 col-md-6">
            <CheckboxComponent v-model="editingBanner.visible" label="Bannière active" class="mb-3" />
          </div>
        </div>

        <TextInputComponent
          v-model="editingBanner.text"
          label="Texte"
          placeholder="Texte affiché dans la bannière"
          :required="true"
          class="mb-3"
        />

        <SelectInputComponent
          v-model="editingBanner.linkType"
          label="Destination"
          placeholder="Sélectionnez une destination"
          :options="linkTypeOptions"
          :required="true"
          class="mb-3"
        />

        <TextInputComponent
          v-if="editingBanner.linkType === BannerLinkTypeEnum.Link"
          v-model="editingBanner.link"
          label="Lien"
          placeholder="https://..."
          type="url"
          :required="true"
          class="mb-3"
        />

        <template v-else-if="editingBanner.linkType === BannerLinkTypeEnum.Vimeo">
          <TextInputComponent
            v-model="editingBanner.videoKey"
            label="Clé de la vidéo Vimeo"
            placeholder="Identifiant de la vidéo Vimeo"
            :required="true"
            class="mb-3"
          />
          <TextInputComponent
            v-model="editingBanner.videoTitle"
            label="Titre de la vidéo"
            placeholder="Titre affiché dans le lecteur vidéo"
            :required="false"
            class="mb-3"
          />
        </template>

        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary" :disabled="isSubmitting" @click="cancelEdit">Annuler</button>
          <button class="btn btn-success" :disabled="isSubmitting" @click="saveBanner">
            <i class="fa-solid fa-floppy-disk pe-2"></i>Enregistrer
          </button>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-hover align-middle">
        <thead>
          <tr>
            <th>Événement</th>
            <th>Texte</th>
            <th>Destination</th>
            <th class="text-center">Active</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="banner in banners" :key="banner.id">
            <td>{{ banner.travelName }}</td>
            <td>{{ banner.text }}</td>
            <td>{{ getLinkTypeLabel(banner.linkType) }}</td>
            <td class="text-center">
              <i v-if="banner.visible" class="fa-solid fa-check text-success"></i>
              <i v-else class="fa-solid fa-minus text-muted"></i>
            </td>
            <td class="text-center text-nowrap">
              <button class="btn btn-link px-2" title="Modifier la bannière" @click="openEdit(banner)">
                <i class="fa-solid fa-pencil"></i>
              </button>
              <button class="btn btn-link px-2" title="Supprimer la bannière" @click="deleteBanner(banner)">
                <i class="fa-regular fa-trash-can"></i>
              </button>
            </td>
          </tr>
          <tr v-if="banners.length === 0">
            <td colspan="5" class="text-center text-muted py-4">Aucune bannière configurée.</td>
          </tr>
        </tbody>
      </table>
    </div>
    </section>
  </AdminPageView>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import adminApi from '@/api/adminApi';
import AdminPageView from '@/views/admin/AdminPageView.vue';
import SelectInputComponent from '@/components/common/SelectInputComponent.vue';
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import CheckboxComponent from '@/components/common/CheckboxComponent.vue';
import { isBoolSetting, useConfirmDialog } from '@/compositions/helper';
import { useNotificationsStore } from '@/stores/notificationStore';

interface BannerForm {
  id: number;
  travelId: number;
  visible: boolean;
  text: string;
  linkType: BannerLinkTypeEnum;
  link: string;
  videoKey: string;
  videoTitle: string;
}

const notificationsStore = useNotificationsStore();
const { confirmDialog } = useConfirmDialog();

const loading = ref(true);
const loadingErrorMessage = ref<string>();
const isSubmitting = ref(false);
const settings = ref<UserSetting[]>([]);
const banners = ref<AdminBanner[]>([]);
const travels = ref<AdminTravelBasic[]>([]);
const editingBanner = ref<BannerForm | null>(null);

const travelOptions = computed(() => travels.value.map(travel => ({
  value: travel.id,
  label: `${travel.name}${travel.company ? ` (${travel.company})` : ''}`
})));

const linkTypeOptions = [
  { value: BannerLinkTypeEnum.None, label: 'Aucun lien' },
  { value: BannerLinkTypeEnum.Link, label: 'Lien externe' },
  { value: BannerLinkTypeEnum.Vimeo, label: 'Vidéo Vimeo' },
];

const getLinkTypeLabel = (linkType?: BannerLinkTypeEnum) => {
  return linkTypeOptions.find(option => option.value === linkType)?.label ?? 'Aucun lien';
};

const createEmptyForm = (): BannerForm => ({
  id: 0,
  travelId: 0,
  visible: true,
  text: '',
  linkType: BannerLinkTypeEnum.None,
  link: '',
  videoKey: '',
  videoTitle: '',
});

const openCreate = () => {
  editingBanner.value = createEmptyForm();
};

const openEdit = (banner: AdminBanner) => {
  editingBanner.value = {
    id: banner.id,
    travelId: banner.travelId,
    visible: banner.visible ?? false,
    text: banner.text,
    linkType: banner.linkType ?? BannerLinkTypeEnum.None,
    link: banner.link ?? '',
    videoKey: banner.videoKey ?? '',
    videoTitle: banner.videoTitle ?? '',
  };
};

const cancelEdit = () => {
  editingBanner.value = null;
};

const validateForm = (form: BannerForm): string | undefined => {
  if (!form.travelId) return 'Veuillez sélectionner un événement.';
  if (!form.text.trim()) return 'Le texte de la bannière est obligatoire.';
  if (form.linkType === BannerLinkTypeEnum.Link && !form.link.trim()) return 'Le lien est obligatoire.';
  if (form.linkType === BannerLinkTypeEnum.Vimeo && !form.videoKey.trim()) return 'La clé Vimeo est obligatoire.';
  return undefined;
};

const toRequest = (form: BannerForm): AdminBannerRequest => ({
  id: form.id,
  travelId: form.travelId,
  visible: form.visible,
  text: form.text,
  linkType: form.linkType,
  link: form.linkType === BannerLinkTypeEnum.Link ? form.link : null,
  videoKey: form.linkType === BannerLinkTypeEnum.Vimeo ? form.videoKey : null,
  videoTitle: form.linkType === BannerLinkTypeEnum.Vimeo ? form.videoTitle || null : null,
});

const saveStringSetting = (key: string) => {
  const setting = settings.value.find(item => item.key === key);
  if (!setting) return;

  isSubmitting.value = true;
  adminApi.saveStringSettings(setting)
    .catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de la sauvegarde du paramètre.', error.message);
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};

const saveBoolSetting = (key: string) => {
  const setting = settings.value.find(item => item.key === key);
  if (!setting) return;

  isSubmitting.value = true;
  adminApi.saveBoolSettings(setting)
    .catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de la sauvegarde du paramètre.', error.message);
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};

const loadData = async () => {
  const [settingsResponse, bannerResponse, travelResponse] = await Promise.all([
    adminApi.getAllSettings(),
    adminApi.getBanners(),
    adminApi.getTravels(),
  ]);
  settings.value = settingsResponse.data;
  banners.value = bannerResponse.data;
  travels.value = travelResponse.data.items;
};

const saveBanner = async () => {
  if (!editingBanner.value) return;
  const validationError = validateForm(editingBanner.value);
  if (validationError) {
    notificationsStore.notifyError(validationError);
    return;
  }

  isSubmitting.value = true;
  try {
    await adminApi.saveBanner(toRequest(editingBanner.value));
    await loadData();
    editingBanner.value = null;
    notificationsStore.notifySuccess('Bannière enregistrée.');
  } catch (error) {
    notificationsStore.notifyError('Erreur lors de l’enregistrement de la bannière.', (error as Error).message);
  } finally {
    isSubmitting.value = false;
  }
};

const deleteBanner = async (banner: AdminBanner) => {
  if (!await confirmDialog(`Êtes-vous sûr de vouloir supprimer la bannière de l'événement « ${banner.travelName} » ?`)) return;

  try {
    await adminApi.deleteBanner(banner.id);
    banners.value = banners.value.filter(item => item.id !== banner.id);
    notificationsStore.notifySuccess('Bannière supprimée.');
  } catch (error) {
    notificationsStore.notifyError('Erreur lors de la suppression de la bannière.', (error as Error).message);
  }
};

onMounted(async () => {
  try {
    await loadData();
  } catch (error) {
    loadingErrorMessage.value = 'Impossible de charger les bannières.';
    notificationsStore.notifyError('Erreur lors du chargement des bannières.', (error as Error).message);
  } finally {
    loading.value = false;
  }
});
</script>
